import { PreflightTest, runPreflight } from 'twilio-video';
import { isBadQuality, qualityFromPreflight } from './NetworkQuality';
import { Quality } from '../../commands/practitioner/enums/CallQuality';
import { runBitrateTest } from './NetworkBitrate';

export type TokenKind = 'preflight' | 'call';

export type GetTokenFn = (sessionId: string, treatmentId: string, isSendToClient?: boolean) => Promise<string>;

export interface PreflightCache {
  report: any | null;
  lastRunAt: number;
}

export interface RunOptions {
  sessionId: string;
  treatmentId: string;
  getToken: GetTokenFn;
  durationMs?: number;
  recacheMs?: number;
  abort?: AbortSignal;
  cache?: PreflightCache;
  tokenKind?: TokenKind;
}

export interface RunResult {
  report: any | null;
  quality: Quality;
  lastRunAt: number;
  shouldOpen: boolean;
}

const toAbortError = () => new DOMException('Aborted', 'AbortError');

const attachBitrateReport = (preflightReport: any | null, bitrateReport: any | null): any | null => {
  if (!preflightReport && !bitrateReport) return null;
  if (!preflightReport) return { bitrate: bitrateReport };
  return {
    ...preflightReport,
    bitrate: bitrateReport,
  };
};

async function runSinglePreflight(token: string, durationMs: number, abort?: AbortSignal): Promise<any | null> {
  if (abort?.aborted) throw toAbortError();

  return await new Promise<any | null>((resolve, reject) => {
    let test: PreflightTest | null = null;
    let done = false;

    const cleanup = () => {
      try {
        test?.stop();
      } catch {}
      test = null;
      abort?.removeEventListener('abort', onAbort);
    };

    const fail = (error: unknown) => {
      if (done) return;
      done = true;
      cleanup();
      reject(error);
    };

    const finish = (report: any | null) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(report);
    };

    const onAbort = () => {
      fail(toAbortError());
    };

    abort?.addEventListener('abort', onAbort, { once: true });

    try {
      test = runPreflight(token, { duration: durationMs });
    } catch (error) {
      fail(error);
      return;
    }

    test.on('completed', (r: any) => {
      finish(r);
    });

    test.on('failed', (err: unknown) => {
      console.warn('preflight failed:', err);
      finish(null);
    });
  });
}

export async function runOrReusePreflight(opts: RunOptions): Promise<RunResult> {
  const {
    sessionId,
    treatmentId,
    getToken,
    durationMs = 10_000,
    recacheMs = 120_000,
    abort,
    cache,
    tokenKind = 'preflight',
  } = opts;

  if (cache?.report && Date.now() - cache.lastRunAt < recacheMs) {
    const q = qualityFromPreflight(cache.report as any);
    return {
      report: cache.report,
      quality: q,
      lastRunAt: cache.lastRunAt,
      shouldOpen: isBadQuality(q),
    };
  }

  if (abort?.aborted) throw new DOMException('Aborted', 'AbortError');

  const isSendToClient = tokenKind === 'call';
  const token = await getToken(sessionId, treatmentId, isSendToClient);
  if (!token) throw new Error('No token');

  const preflightReport = await runSinglePreflight(token, durationMs, abort);
  const bitrateReport = await runBitrateTest({
    token,
    abort,
    durationMs: 15_000,
    minBitrateThreshold: 800,
  });

  const report = attachBitrateReport(preflightReport, bitrateReport);
  const q: Quality = report ? qualityFromPreflight(report as any) : Quality.Error;
  const now = Date.now();

  return {
    report,
    quality: q,
    lastRunAt: now,
    shouldOpen: isBadQuality(q),
  };
}
